// ============================================================
// ThemeSwitcher
// ============================================================
// A row of colour swatches in the toolbar.
// Applies a theme by setting data-theme on <html>.
// Persists the chosen theme to localStorage.
// Print is unaffected — @media print always overrides.
// ============================================================

import React, { useEffect, useState } from 'react';

// ------------------------------------------------------------
// THEME DEFINITIONS
// ------------------------------------------------------------

export interface Theme {
  id: string;
  label: string;
  /** Swatch colour shown in the toolbar */
  swatch: string;
  /** Second colour for split swatch (paper + ink preview) */
  swatchInk: string;
}

export const THEMES: Theme[] = [
  {
    id:        'manuscript',
    label:     'Manuscript',
    swatch:    '#f9f6f0',
    swatchInk: '#1a1714',
  },
  {
    id:        'parchment',
    label:     'Parchment',
    swatch:    '#f5e6c8',
    swatchInk: '#2c1a08',
  },
  {
    id:        'newsprint',
    label:     'Newsprint',
    swatch:    '#f4f4f2',
    swatchInk: '#111110',
  },
  {
    id:        'blueprint',
    label:     'Blueprint',
    swatch:    '#0d1b2a',
    swatchInk: '#38bdf8',
  },
  {
    id:        'night',
    label:     'Night',
    swatch:    '#12141a',
    swatchInk: '#e8dfc8',
  },
];

const STORAGE_KEY = 'harmonic-charter:theme';
const DEFAULT_THEME = 'manuscript';

// ------------------------------------------------------------
// HOOK: useTheme
// Can be imported elsewhere if needed.
// ------------------------------------------------------------

export function useTheme() {
  const [themeId, setThemeId] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY) ?? DEFAULT_THEME;
  });

  // Apply theme on mount and on change
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeId);
    localStorage.setItem(STORAGE_KEY, themeId);
  }, [themeId]);

  return { themeId, setThemeId };
}

// ------------------------------------------------------------
// COMPONENT
// ------------------------------------------------------------

interface ThemeSwitcherProps {
  themeId: string;
  setThemeId: (id: string) => void;
}

export function ThemeSwitcher({ themeId, setThemeId }: ThemeSwitcherProps) {
  return (
    <div
      className="theme-switcher flex items-center gap-1.5"
      title="Change theme"
      aria-label="Theme switcher"
    >
      {THEMES.map((theme) => (
        <button
          key={theme.id}
          onClick={() => setThemeId(theme.id)}
          title={theme.label}
          aria-label={`${theme.label} theme`}
          className={`theme-swatch ${themeId === theme.id ? 'active' : ''}`}
          style={{
            // Split circle: left half = paper, right half = ink
            background: `linear-gradient(135deg, ${theme.swatch} 50%, ${theme.swatchInk} 50%)`,
            borderColor: themeId === theme.id
              ? 'var(--color-ink-primary)'
              : 'var(--color-rule)',
          }}
        />
      ))}
    </div>
  );
}
